/** Recognition metadata uses the operator's monotonic clock, never wall-clock time. */
export interface TranscriptStamp {
  epoch: number;
  captureEndMs: number;
  utteranceId: string;
  /** Increases for each interim/final revision within this utterance. */
  sequence: number;
}

export type TranscriptDecision = {
  accepted: boolean;
  reason:
    | "accepted"
    | "invalid"
    | "stale-epoch"
    | "before-boundary"
    | "out-of-order";
};

/**
 * Prevents delayed recognition from undoing an operator's position changes.
 *
 * Invalidate on seek, reset, script replacement, pause and resume. Stamp audio
 * when it is captured/submitted, before asynchronous recognition starts. A
 * continuing utterance retains its original epoch until the engine starts a
 * new segment; stamping an old result on arrival would defeat this protection.
 *
 * This guard does not compare words or positions. A fresh utterance can repeat
 * an earlier passage intentionally; that decision belongs to the matcher.
 */
export class TranscriptPositionGuard {
  private currentEpoch = 0;
  private boundaryMs = 0;
  private latestCaptureEndMs = 0;
  private utterances = new Map<string, number>();

  constructor(private readonly clock: () => number = () => performance.now()) {}

  get epoch() {
    return this.currentEpoch;
  }

  get notBeforeMs() {
    return this.boundaryMs;
  }

  invalidate(atMs = this.clock()): number {
    if (!Number.isFinite(atMs) || atMs < 0)
      throw new RangeError("Transcript boundary requires a monotonic timestamp.");
    this.currentEpoch++;
    this.boundaryMs = Math.max(this.boundaryMs, atMs);
    this.latestCaptureEndMs = this.boundaryMs;
    this.utterances.clear();
    return this.currentEpoch;
  }

  stamp(
    captureEndMs: number,
    utteranceId: string,
    sequence: number,
  ): TranscriptStamp {
    return { epoch: this.currentEpoch, captureEndMs, utteranceId, sequence };
  }

  accept(stamp: TranscriptStamp): TranscriptDecision {
    const reject = (reason: TranscriptDecision["reason"]): TranscriptDecision =>
      ({ accepted: false, reason });
    if (
      !stamp ||
      !Number.isSafeInteger(stamp.epoch) ||
      stamp.epoch < 0 ||
      !Number.isFinite(stamp.captureEndMs) ||
      stamp.captureEndMs < 0 ||
      typeof stamp.utteranceId !== "string" ||
      !stamp.utteranceId.trim() ||
      stamp.utteranceId.length > 512 ||
      !Number.isSafeInteger(stamp.sequence) ||
      stamp.sequence < 0
    )
      return reject("invalid");
    if (stamp.epoch !== this.currentEpoch) return reject("stale-epoch");
    if (stamp.captureEndMs < this.boundaryMs) return reject("before-boundary");
    const previousSequence = this.utterances.get(stamp.utteranceId);
    if (
      stamp.captureEndMs < this.latestCaptureEndMs ||
      (previousSequence !== undefined && stamp.sequence <= previousSequence)
    )
      return reject("out-of-order");

    this.latestCaptureEndMs = stamp.captureEndMs;
    // Recent IDs detect duplicate/reordered revisions without retaining every
    // utterance from a full production day. The audio watermark rejects older
    // results even after their ID leaves this bounded history.
    this.utterances.delete(stamp.utteranceId);
    this.utterances.set(stamp.utteranceId, stamp.sequence);
    if (this.utterances.size > 128)
      this.utterances.delete(this.utterances.keys().next().value!);
    return { accepted: true, reason: "accepted" };
  }
}
